import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Clock3, ShieldCheck, CheckCircle2, XCircle, Plus, X } from "lucide-react";
import { useCart } from "../user/CartContext";
import { useCityPrice } from "../../hooks/useCityPrice";
import { services } from "./HomeServices";

// Instant bookings are capped at 4 Hrs total (8 blocks of 30 min) — this
// now covers the main service AND every add-on combined, not just the
// main service on its own.
const MAX_BLOCKS = 8;

// ─────────────────────────────────────────────────────────────────────────────
// Reached via "Book Now" on any duration-sold Instant card on the catalog
// page (Instant CN Pro/Plus + every Quick Services Access parity service —
// see HomeServices.js's `durationSold` flag). The catalog card itself only
// shows image + title + Details/Book Now; duration picking, the price
// summary and basic details all live here instead.
//
// Add-Ons (service.addOns, an array of real catalog service ids — see
// hs-1's addOns in HomeServices.js) are clickable: clicking one opens a
// duration-picker popup, and confirming adds it as its own line in the
// price summary. The combined duration of the main service + every added
// add-on can never exceed MAX_BLOCKS.
// ─────────────────────────────────────────────────────────────────────────────
const formatDuration = (blocks) => {
  const totalMin = blocks * 30;
  const hrs = Math.floor(totalMin / 60);
  const min = totalMin % 60;
  if (hrs === 0) return `${min} min`;
  if (min === 0) return `${hrs} Hr${hrs > 1 ? "s" : ""}`;
  return `${hrs} Hr${hrs > 1 ? "s" : ""} ${min} min`;
};

const InstantServiceDetailPage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { addItem, setQty } = useCart();
  const { cityPrice } = useCityPrice();
  const [blocks, setBlocks] = useState(1);
  const [selectedAddOns, setSelectedAddOns] = useState([]); // [{ id, blocks }]
  const [addOnModalId, setAddOnModalId] = useState(null);
  const [addOnModalBlocks, setAddOnModalBlocks] = useState(1);

  const service = services.find(
    (s) => s.id === serviceId && (s.isSpecial || s.isInstant)
  );

  if (!service) {
    return (
      <div style={S.page}>
        <div style={{ ...S.container, textAlign: "center", paddingTop: "80px" }}>
          <h2>Service not found</h2>
          <Link to="/services/instant-home-services" style={S.backLink}>
            <ArrowLeft size={14} /> Back to Instant Home Services
          </Link>
        </div>
      </div>
    );
  }

  const unitPrice = cityPrice(service.id, service.price);

  // Resolve each add-on id to its real catalog entry (name/image/price) —
  // single source of truth, no separate name/image lookup to keep in sync.
  const addOnServices = (service.addOns || [])
    .map((id) => services.find((s) => s.id === id))
    .filter(Boolean);

  const getSelectedAddOn = (id) => selectedAddOns.find((a) => a.id === id);
  const addOnBlocksTotal = selectedAddOns.reduce((sum, a) => sum + a.blocks, 0);
  const totalBlocks = blocks + addOnBlocksTotal;

  const addOnLineTotal = (addon, addOnBlocks) => cityPrice(addon.id, addon.price) * addOnBlocks;
  const mainTotal = unitPrice * blocks;
  const total = mainTotal + selectedAddOns.reduce((sum, a) => {
    const addon = addOnServices.find((s) => s.id === a.id);
    return sum + (addon ? addOnLineTotal(addon, a.blocks) : 0);
  }, 0);

  // Budget left for a given add-on id — total cap minus the main service
  // and every OTHER already-added add-on (an add-on's own current blocks
  // don't count against itself while editing it). Can be 0 if the main
  // service + other add-ons already use the full 4 Hrs.
  const maxBlocksForAddOn = (id) => {
    const otherUsed = blocks + selectedAddOns
      .filter((a) => a.id !== id)
      .reduce((sum, a) => sum + a.blocks, 0);
    return Math.max(0, MAX_BLOCKS - otherUsed);
  };

  const openAddOnModal = (id) => {
    const existing = getSelectedAddOn(id);
    // Computed directly for `id`, not read from the (still-stale, from the
    // previous render) addOnModalMaxBlocks below — this runs before the
    // setAddOnModalId state update below has taken effect.
    setAddOnModalId(id);
    setAddOnModalBlocks(existing ? existing.blocks : Math.min(1, maxBlocksForAddOn(id)));
  };
  const closeAddOnModal = () => setAddOnModalId(null);

  // Same budget calculation, but for whichever add-on is currently open in
  // the modal (addOnModalId has settled by the time this renders).
  const addOnModalMaxBlocks = maxBlocksForAddOn(addOnModalId);

  const confirmAddOnModal = () => {
    setSelectedAddOns((prev) => {
      const without = prev.filter((a) => a.id !== addOnModalId);
      return [...without, { id: addOnModalId, blocks: addOnModalBlocks }];
    });
    closeAddOnModal();
  };

  const removeAddOn = (id) => {
    setSelectedAddOns((prev) => prev.filter((a) => a.id !== id));
    if (addOnModalId === id) closeAddOnModal();
  };

  const modalAddOn = addOnServices.find((s) => s.id === addOnModalId) || null;

  // Same one-click Instant flow the main service used to run alone: add the
  // duration-priced main service PLUS every selected add-on as its own
  // line, then go straight to the Instant checkout (confirm location ->
  // search for a partner) — same destination every other Instant booking
  // ends up at.
  const handleConfirm = () => {
    const mainCartItem = {
      id:       service.id,
      title:    service.name,
      name:     service.name,
      subtitle: formatDuration(blocks),
      price:    unitPrice,
      image:    typeof service.image === "string" ? service.image : "",
      category: "Home Services",
      qty:      blocks,
    };
    const addOnCartItems = selectedAddOns.map((a) => {
      const addon = addOnServices.find((s) => s.id === a.id);
      return {
        id:       addon.id,
        title:    addon.name,
        name:     addon.name,
        subtitle: formatDuration(a.blocks),
        price:    cityPrice(addon.id, addon.price),
        image:    typeof addon.image === "string" ? addon.image : "",
        category: "Home Services",
        qty:      a.blocks,
      };
    });
    const cartItems = [mainCartItem, ...addOnCartItems];
    cartItems.forEach((item) => {
      addItem(item);
      setQty(item.id, item.qty);
    });
    // Pass the items via navigation state instead of relying on localStorage —
    // CartContext's own persist-to-localStorage effect runs asynchronously
    // after commit, so navigating immediately after addItem/setQty can land
    // on the checkout page before it's flushed. InstantSummaryPage.js's
    // resolveCart() already prefers location.state.cartItems for this.
    navigate("/checkout-instant/summary", { state: { cartItems } });
  };

  return (
    <div style={S.page}>
      <div style={S.container}>
        <Link to="/services/instant-home-services" style={S.backLink}>
          <ArrowLeft size={14} /> Back to Instant Home Services
        </Link>

        <div style={S.layout}>
          {/* Left: duration picker + price summary — bigger, the primary
              decision on this page, so it leads rather than trailing in a
              narrow sidebar. */}
          <div style={S.left}>
            <div style={S.summaryCard}>
              <div style={S.durationHead}><Clock3 size={20} color="#4f46e5" /> Select Duration</div>
              <div style={S.durationRow}>
                <button
                  type="button"
                  style={blocks <= 1 ? S.stepBtnDisabled : S.stepBtn}
                  onClick={() => setBlocks((b) => Math.max(1, b - 1))}
                  disabled={blocks <= 1}
                >
                  −
                </button>
                <span style={S.durationLabel}>{formatDuration(blocks)}</span>
                <button
                  type="button"
                  style={totalBlocks >= MAX_BLOCKS ? S.stepBtnDisabled : S.stepBtnP}
                  onClick={() => setBlocks((b) => Math.min(MAX_BLOCKS - addOnBlocksTotal, b + 1))}
                  disabled={totalBlocks >= MAX_BLOCKS}
                >
                  +
                </button>
              </div>
              <div style={S.durationHint}>30 min – 4 Hrs total, in 30-min steps</div>

              <div style={S.summaryDivider} />

              <div style={S.summaryRow}><span>{service.name}</span><span>₹{unitPrice} / 30 min</span></div>
              <div style={S.summaryRow}><span>Duration</span><span>{formatDuration(blocks)}</span></div>

              {selectedAddOns.length > 0 && (
                <>
                  <div style={S.summaryDivider} />
                  <div style={S.addOnSummaryLabel}>Add-Ons</div>
                  {selectedAddOns.map((a) => {
                    const addon = addOnServices.find((s) => s.id === a.id);
                    if (!addon) return null;
                    return (
                      <div key={a.id} style={S.addOnSummaryRow}>
                        <div style={S.addOnSummaryInfo}>
                          <div style={S.addOnSummaryName}>{addon.name}</div>
                          <div style={S.addOnSummaryMeta}>{formatDuration(a.blocks)} · ₹{addOnLineTotal(addon, a.blocks)}</div>
                        </div>
                        <button type="button" style={S.addOnRemoveBtn} onClick={() => removeAddOn(a.id)} title="Remove">
                          <X size={13} />
                        </button>
                      </div>
                    );
                  })}
                </>
              )}

              <div style={S.summaryDivider} />
              <div style={S.summaryRow}><span>Total Duration</span><span>{formatDuration(totalBlocks)} (max 4 Hrs)</span></div>
              <div style={{ ...S.summaryRow, ...S.summaryTotal }}><span>Total</span><span>₹{total}</span></div>

              <button style={S.confirmBtn} onClick={handleConfirm}>
                Continue — ₹{total}
              </button>
              <div style={S.secureNote}><ShieldCheck size={13} /> Pay after your professional arrives</div>
            </div>
          </div>

          {/* Right: title + description, then Add-On Services (small QAS_*
              thumbnails) in place of the old hero image — click one to add
              it to the booking. */}
          <div style={S.right}>
            {(service.isSpecial || service.isVerified) && (
              <div style={S.badgeRow}>
                {service.isSpecial && <span style={S.featuredBadge}>⭐ Featured</span>}
                {service.isVerified && <span style={S.verifiedBadge}>✅ Verified</span>}
              </div>
            )}
            <h1 style={S.title}>{service.name}</h1>
            <p style={S.desc}>{service.desc}</p>

            {addOnServices.length > 0 && (
              <div style={S.addOnSection}>
                <div style={S.detailLabel}><Plus size={14} color="#4f46e5" /> Add-On Services</div>
                {/* Single grid, 4 fixed-width columns — the browser's own
                    grid auto-flow wraps and left-aligns a trailing partial
                    row cleanly, no manual row-chunking needed. */}
                <div style={S.addOnGrid}>
                  {addOnServices.map((addon) => {
                    const added = getSelectedAddOn(addon.id);
                    return (
                      <button
                        type="button"
                        key={addon.id}
                        style={{ ...S.addOnCard, ...(added ? S.addOnCardActive : {}) }}
                        onClick={() => openAddOnModal(addon.id)}
                      >
                        {typeof addon.image === "string" ? (
                          // QAS_* artwork already has the name baked in —
                          // no separate text label needed.
                          <img src={addon.image} alt={addon.name} style={S.addOnImg} />
                        ) : (
                          // No image on this catalog entry — fall back to a
                          // plain text label so the card isn't blank.
                          <div style={S.addOnName}>{addon.name}</div>
                        )}
                        {added && (
                          <div style={S.addOnAddedBadge}>
                            <CheckCircle2 size={12} /> {formatDuration(added.blocks)}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Below summary + image: What we do / won't do, full width so the
            two lists sit side by side instead of stacking in one column. */}
        {((Array.isArray(service.includes) && service.includes.length > 0) ||
          (Array.isArray(service.excludes) && service.excludes.length > 0)) && (
          <div style={S.doDontGrid}>
            {Array.isArray(service.includes) && service.includes.length > 0 && (
              <div style={S.doDontPanel}>
                <div style={S.detailLabel}><CheckCircle2 size={14} color="#16a34a" /> What's Included</div>
                <ul style={S.list}>
                  {service.includes.map((item) => (
                    <li key={item} style={S.listItem}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {Array.isArray(service.excludes) && service.excludes.length > 0 && (
              <div style={S.doDontPanel}>
                <div style={S.detailLabel}><XCircle size={14} color="#dc2626" /> Not Included</div>
                <ul style={S.list}>
                  {service.excludes.map((item) => (
                    <li key={item} style={S.listItem}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Customer Notes — last section on the page. Either a single
            sentence (string) or a bulleted list (array), e.g. CN Pro's
            longer scope notes. */}
        {service.customerNotes && (
          <div style={S.notesBox}>
            <div style={S.notesLabel}>📝 Customer Notes</div>
            {Array.isArray(service.customerNotes) ? (
              <ul style={S.notesList}>
                {service.customerNotes.map((note) => (
                  <li key={note} style={S.notesListItem}>{note}</li>
                ))}
              </ul>
            ) : (
              <p style={S.notesText}>{service.customerNotes}</p>
            )}
          </div>
        )}
      </div>

      {/* Add-on duration popup */}
      {modalAddOn && (
        <div style={S.modalOverlay} onClick={closeAddOnModal}>
          <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <div>
                <h3 style={S.modalTitle}>{modalAddOn.name}</h3>
                <div style={S.modalSub}>Select how long you need this add-on for</div>
              </div>
              <button type="button" style={S.modalCloseBtn} onClick={closeAddOnModal}>
                <X size={18} />
              </button>
            </div>

            {typeof modalAddOn.image === "string" && (
              <img src={modalAddOn.image} alt={modalAddOn.name} style={S.modalImg} />
            )}

            <div style={S.durationRow}>
              <button
                type="button"
                style={addOnModalBlocks <= 1 ? S.stepBtnDisabled : S.stepBtn}
                onClick={() => setAddOnModalBlocks((b) => Math.max(1, b - 1))}
                disabled={addOnModalBlocks <= 1}
              >
                −
              </button>
              <span style={S.durationLabel}>{formatDuration(addOnModalBlocks)}</span>
              <button
                type="button"
                style={addOnModalBlocks >= addOnModalMaxBlocks ? S.stepBtnDisabled : S.stepBtnP}
                onClick={() => setAddOnModalBlocks((b) => Math.min(addOnModalMaxBlocks, b + 1))}
                disabled={addOnModalBlocks >= addOnModalMaxBlocks}
              >
                +
              </button>
            </div>
            <div style={S.durationHint}>
              {addOnModalMaxBlocks <= 0
                ? "No time left — your main service + other add-ons already use the full 4 Hrs"
                : addOnModalMaxBlocks < MAX_BLOCKS
                ? `Up to ${formatDuration(addOnModalMaxBlocks)} left in your 4-Hr total`
                : "30 min – 4 Hrs, in 30-min steps"}
            </div>

            <div style={S.summaryDivider} />
            <div style={S.summaryRow}>
              <span>Price</span>
              <span>₹{addOnLineTotal(modalAddOn, addOnModalBlocks)}</span>
            </div>

            <div style={S.modalBtnRow}>
              {getSelectedAddOn(modalAddOn.id) && (
                <button type="button" style={S.modalRemoveBtn} onClick={() => removeAddOn(modalAddOn.id)}>
                  Remove
                </button>
              )}
              <button
                type="button"
                style={{ ...S.confirmBtn, flex: 1, width: "auto", margin: 0, ...(addOnModalBlocks <= 0 ? S.confirmBtnDisabled : {}) }}
                disabled={addOnModalBlocks <= 0}
                onClick={confirmAddOnModal}
              >
                {getSelectedAddOn(modalAddOn.id) ? "Update" : "Add to Booking"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const S = {
  page:      { background: "#f8fafc", minHeight: "100vh", padding: "28px 20px 60px", fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif", color: "#111827" },
  container: { maxWidth: "1180px", margin: "0 auto" },
  backLink:  { display: "inline-flex", alignItems: "center", gap: "6px", marginBottom: "18px", color: "#4f46e5", fontWeight: 700, fontSize: "14px", textDecoration: "none" },

  layout: { display: "grid", gridTemplateColumns: "380px 1fr", gap: "24px", alignItems: "start" },
  left:   { minWidth: 0 },
  right:  { minWidth: 0 },

  badgeRow: { display: "flex", gap: "8px", marginBottom: "10px" },
  featuredBadge: { display: "inline-block", background: "linear-gradient(135deg,#f43f5e,#e11d48)", color: "#fff", fontSize: "12px", fontWeight: 800, padding: "4px 12px", borderRadius: "999px" },
  verifiedBadge: { display: "inline-block", background: "#ecfdf5", color: "#15803d", border: "1px solid #86efac", fontSize: "12px", fontWeight: 800, padding: "4px 12px", borderRadius: "999px" },

  title: { margin: "0 0 8px", fontSize: "24px", fontWeight: 800 },
  desc:  { margin: 0, fontSize: "14px", lineHeight: 1.6, color: "#6b7280" },

  // Add-On Services — small QAS_* thumbnails, replaces the old hero image
  // in the right column. 4 fixed-width columns; a trailing partial row
  // left-aligns naturally via the grid's own auto-flow. Cards are real
  // buttons now — clicking one opens the duration popup.
  addOnSection: { marginTop: "22px" },
  addOnGrid: { display: "grid", gridTemplateColumns: "repeat(4, 180px)", gap: "14px", marginBottom: "12px" },
  addOnCard: { position: "relative", width: "180px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "12px", overflow: "hidden", textAlign: "center", padding: 0, cursor: "pointer" },
  addOnCardActive: { border: "2px solid #4f46e5" },
  addOnImg:  { width: "100%", aspectRatio: "1 / 1", objectFit: "cover", display: "block" },
  // Fallback only — used when an add-on has no matching catalog image.
  addOnName: { fontSize: "12.5px", fontWeight: 700, color: "#374151", padding: "16px 10px", lineHeight: 1.3, minHeight: "180px", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc" },
  addOnAddedBadge: { position: "absolute", bottom: "8px", left: "8px", right: "8px", display: "flex", alignItems: "center", justifyContent: "center", gap: "4px", background: "#4f46e5", color: "#fff", fontSize: "11px", fontWeight: 800, padding: "4px 8px", borderRadius: "999px" },

  // Add-on line items inside the left summary card.
  addOnSummaryLabel: { fontSize: "12px", fontWeight: 800, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "8px" },
  addOnSummaryRow:  { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", marginBottom: "8px" },
  addOnSummaryInfo: { minWidth: 0 },
  addOnSummaryName: { fontSize: "13px", fontWeight: 700, color: "#111827" },
  addOnSummaryMeta: { fontSize: "11.5px", color: "#9ca3af", marginTop: "2px" },
  addOnRemoveBtn:   { width: "26px", height: "26px", flexShrink: 0, borderRadius: "50%", border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },

  // Full-width "What we do / won't do" row below the summary+image row —
  // side by side instead of stacked, so two short lists don't add as much
  // page height as they did stacked in the narrow right column.
  doDontGrid:  { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginTop: "28px" },
  doDontPanel: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "18px 20px" },
  detailLabel: { display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 800, color: "#111827", marginBottom: "10px" },
  list:        { margin: 0, padding: "0 0 0 18px", display: "flex", flexDirection: "column", gap: "6px" },
  listItem:    { fontSize: "13px", color: "#4b5563", lineHeight: 1.5 },

  notesBox:   { background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "12px", padding: "14px 16px", marginTop: "16px" },
  notesLabel: { fontSize: "13px", fontWeight: 800, color: "#92400e", marginBottom: "6px" },
  notesText:  { margin: 0, fontSize: "13px", color: "#78350f", lineHeight: 1.6 },
  notesList:    { margin: 0, padding: "0 0 0 18px", display: "flex", flexDirection: "column", gap: "6px" },
  notesListItem:{ fontSize: "13px", color: "#78350f", lineHeight: 1.6 },

  summaryCard: { position: "sticky", top: "20px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "20px", padding: "26px" },
  durationHead: { display: "flex", alignItems: "center", gap: "9px", fontSize: "16px", fontWeight: 800, color: "#111827", marginBottom: "20px" },
  durationRow:  { display: "flex", alignItems: "center", justifyContent: "center", gap: "22px", marginBottom: "10px" },
  stepBtn:      { width: "52px", height: "52px", borderRadius: "50%", border: "1.5px solid #e4e7ef", background: "#fff", color: "#374151", fontSize: "26px", fontWeight: 700, cursor: "pointer", flexShrink: 0, lineHeight: 1 },
  stepBtnP:     { width: "52px", height: "52px", borderRadius: "50%", border: "none", background: "#4f46e5", color: "#fff", fontSize: "26px", fontWeight: 700, cursor: "pointer", flexShrink: 0, lineHeight: 1 },
  stepBtnDisabled: { width: "52px", height: "52px", borderRadius: "50%", border: "1.5px solid #eceef5", background: "#f3f4f6", color: "#c1c5cf", fontSize: "26px", fontWeight: 700, cursor: "not-allowed", flexShrink: 0, lineHeight: 1 },
  durationLabel: { fontSize: "26px", fontWeight: 800, color: "#111827", minWidth: "140px", textAlign: "center" },
  durationHint:  { fontSize: "12.5px", color: "#9ca3af", textAlign: "center", marginBottom: "22px" },

  summaryDivider: { height: "1px", background: "#eee", margin: "16px 0" },
  summaryRow:  { display: "flex", justifyContent: "space-between", fontSize: "14px", color: "#6b7280", marginBottom: "9px", gap: "10px" },
  summaryTotal: { fontSize: "18px", fontWeight: 800, color: "#111827", marginBottom: "0" },

  confirmBtn: { width: "100%", border: "none", borderRadius: "14px", padding: "16px 0", fontSize: "15.5px", fontWeight: 800, background: "linear-gradient(135deg,#4f46e5,#4338ca)", color: "#fff", marginTop: "20px", marginBottom: "10px", cursor: "pointer" },
  confirmBtnDisabled: { background: "#c7cad1", cursor: "not-allowed" },
  secureNote: { display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", fontSize: "11px", color: "#9ca3af", fontWeight: 600 },

  // Add-on duration popup.
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "20px" },
  modalCard: { background: "#fff", borderRadius: "20px", padding: "22px", width: "100%", maxWidth: "380px" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px", marginBottom: "14px" },
  modalTitle: { margin: 0, fontSize: "17px", fontWeight: 800 },
  modalSub:   { margin: "4px 0 0", fontSize: "12.5px", color: "#6b7280" },
  modalCloseBtn: { border: "none", background: "#f3f4f6", color: "#6b7280", width: "30px", height: "30px", borderRadius: "50%", cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" },
  modalImg: { width: "100%", aspectRatio: "16 / 9", objectFit: "cover", borderRadius: "12px", display: "block", marginBottom: "16px" },
  modalBtnRow: { display: "flex", gap: "10px", marginTop: "18px" },
  modalRemoveBtn: { flex: "0 0 auto", padding: "0 18px", height: "48px", borderRadius: "14px", border: "1.5px solid #fecaca", background: "#fef2f2", color: "#dc2626", fontWeight: 800, fontSize: "14px", cursor: "pointer" },
};

export default InstantServiceDetailPage;
